'use client';

import { c as _c } from "react/compiler-runtime";
import type { MergeabilityStatusResponse } from '@/lib/hooks/mergeability-status/query-data';
import type { PrV2DiscussionResponse, PrV2MergeRequest, PrV2MergeResponse, PrV2SummaryResponse } from '@/lib/pr-v2/types';
import type { QueryKey } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { mergePrV2 } from './fetch-pr-v2';
import { recomputePrV2MergeabilityStatus } from './mergeability-utils';
import { prV2DiscussionQueryKey, prV2SummaryQueryKey } from './query-keys';
import { patchCodeSessionPullRequests, refetchLoadingCodeSessionPullRequests } from '@/data/code-sessions/patch-pull-request';
import { patchInboxPullRequest } from '@/data/pull-requests/patch-inbox-pull-request';
import { inboxV2MergedPullRequestsQueryKeyPrefix, inboxV2OpenPullRequestsQueryKeyPrefix } from '@/data/pull-requests/query-keys';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { mergeabilityStatusQueryKey } from '@/lib/hooks/mergeability-status/keys';
import { TIMELINE_ITEM_TYPE } from '@/lib/pr-v2/timeline-types';
import { usePrStore } from '@/lib/stores/pr-store';
import { useAuth } from '@/providers/auth-provider';
const MERGE_SUCCESS_TOASTS: Record<PrV2MergeResponse['status'], string> = {
  merged: 'Pull request merged',
  enqueued: 'Added to merge queue',
  pending: 'GitHub is still merging. It shows as merged once it finishes.'
};
type MergeTarget = {
  owner: string;
  repo: string;
  number: string;
  installationId: number | null;
};
function mergeTargetQueryKeys(target: MergeTarget) {
  return {
    summaryKey: prV2SummaryQueryKey(target.owner, target.repo, target.number),
    discussionKey: prV2DiscussionQueryKey(target.owner, target.repo, target.number),
    mergeabilityKey: mergeabilityStatusQueryKey(target.owner, target.repo, target.number, 'v2')
  };
}
function appendOptimisticMergeEvent(current: PrV2DiscussionResponse | undefined, summary: PrV2SummaryResponse['pullRequest'] | undefined, mergeCommitSha: string, userLogin: string, userAvatarUrl: string | null): PrV2DiscussionResponse | undefined {
  const nextItem = {
    id: `optimistic-merge-${Date.now()}`,
    type: TIMELINE_ITEM_TYPE.MergeCommit,
    timestamp: new Date().toISOString(),
    node_id: `optimistic-merge-${Date.now()}`,
    user: {
      login: userLogin,
      avatar_url: userAvatarUrl ?? '',
      html_url: ''
    },
    html_url: summary?.url ?? '',
    merge_commit_sha: mergeCommitSha
  } as PrV2DiscussionResponse['timelineItems'][number];
  if (!current) {
    return {
      syncState: 'ready',
      timelineItems: [nextItem],
      commitCheckSuites: [],
      reactions: {},
      cachedAt: null
    };
  }
  return {
    ...current,
    timelineItems: [...current.timelineItems, nextItem]
  };
}
export function usePrV2MergeAction(owner, repo, number) {
  const $ = _c(32);
  const queryClient = useQueryClient();
  const {
    user
  } = useAuth();
  const {
    installationId
  } = usePrStore();
  let t0;
  if ($[0] !== user) {
    t0 = user?.user_metadata && typeof user.user_metadata === "object" ? user.user_metadata as Record<string, unknown> : {};
    $[0] = user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const authMetadata = t0;
  let t1;
  if ($[2] !== authMetadata.user_name || $[3] !== user) {
    t1 = typeof authMetadata.user_name === "string" && authMetadata.user_name.trim() || typeof user?.email === "string" && user.email.trim() || "you";
    $[2] = authMetadata.user_name;
    $[3] = user;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const userLogin = t1;
  const userAvatarUrl = typeof authMetadata.avatar_url === "string" ? authMetadata.avatar_url : null;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [inboxV2OpenPullRequestsQueryKeyPrefix(), inboxV2MergedPullRequestsQueryKeyPrefix()];
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const inboxInvalidationTargets = t2;
  let t3;
  if ($[6] !== queryClient) {
    t3 = async queryKeys => {
      await Promise.all(queryKeys.map(queryKey => queryClient.invalidateQueries({
        queryKey
      })));
    };
    $[6] = queryClient;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const refetch = t3;
  let t4;
  if ($[8] !== queryClient || $[9] !== refetch || $[10] !== userAvatarUrl || $[11] !== userLogin) {
    t4 = async (target, mergeCommitSha) => {
      const {
        summaryKey,
        discussionKey,
        mergeabilityKey
      } = mergeTargetQueryKeys(target);
      await Promise.all([queryClient.cancelQueries({
        queryKey: summaryKey
      }), queryClient.cancelQueries({
        queryKey: discussionKey
      }), queryClient.cancelQueries({
        queryKey: mergeabilityKey
      }), patchCodeSessionPullRequests(queryClient, target, {
        merged: true
      })]);
      await refetchLoadingCodeSessionPullRequests(queryClient);
      const summary = queryClient.getQueryData(summaryKey);
      queryClient.setQueryData(summaryKey, _temp);
      queryClient.setQueryData(discussionKey, current_0 => appendOptimisticMergeEvent(current_0, summary?.pullRequest, mergeCommitSha, userLogin, userAvatarUrl));
      queryClient.setQueryData(mergeabilityKey, _temp3);
      patchInboxPullRequest(queryClient, {
        owner: target.owner,
        repo: target.repo,
        number: Number(target.number)
      }, _temp4);
      await Promise.all([...[summaryKey, discussionKey, mergeabilityKey].map(queryKey_0 => queryClient.invalidateQueries({
        queryKey: queryKey_0,
        refetchType: "none" as const
      })), refetch(inboxInvalidationTargets)]);
    };
    $[8] = queryClient;
    $[9] = refetch;
    $[10] = userAvatarUrl;
    $[11] = userLogin;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const showMerged = t4;
  let t5;
  if ($[13] !== number || $[14] !== owner || $[15] !== repo) {
    t5 = async variables => {
      const promise = mergePrV2(owner, repo, number, variables);
      toast.promise(promise, {
        loading: variables.merge_action === "queue" ? "Adding to merge queue..." : "Merging pull request...",
        success: _temp5,
        error: _temp6
      });
      return promise;
    };
    $[13] = number;
    $[14] = owner;
    $[15] = repo;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== installationId || $[18] !== number || $[19] !== owner || $[20] !== repo) {
    t6 = () => ({
      owner,
      repo,
      number,
      installationId
    });
    $[17] = installationId;
    $[18] = number;
    $[19] = owner;
    $[20] = repo;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== refetch || $[23] !== showMerged) {
    t7 = async (outcome_0, variables_0, target_0) => {
      bb78: switch (outcome_0.status) {
        case "merged":
          {
            await showMerged(target_0, outcome_0.sha);
            break bb78;
          }
        case "enqueued":
          {
            const {
              summaryKey: summaryKey_0,
              mergeabilityKey: mergeabilityKey_0
            } = mergeTargetQueryKeys(target_0);
            await refetch([summaryKey_0, mergeabilityKey_0, ...inboxInvalidationTargets]);
            break bb78;
          }
        case "pending":
          {
            return;
          }
        default:
          {
            const exhaustiveCheck = outcome_0;
            return exhaustiveCheck;
          }
      }
      trackClientEvent("v2_pr_merged", target_0.installationId?.toString(), {
        owner: target_0.owner,
        repo: target_0.repo,
        pr_number: target_0.number,
        merge_method: variables_0.merge_method,
        admin_bypass: variables_0.merge_action === "bypass",
        outcome: outcome_0.status
      });
    };
    $[22] = refetch;
    $[23] = showMerged;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== refetch) {
    t8 = async (_error, _variables, target_1) => {
      if (!target_1) {
        return;
      }
      const {
        summaryKey: summaryKey_1,
        mergeabilityKey: mergeabilityKey_1
      } = mergeTargetQueryKeys(target_1);
      await refetch([summaryKey_1, mergeabilityKey_1]);
    };
    $[25] = refetch;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t5 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8) {
    t9 = {
      mutationFn: t5,
      onMutate: t6,
      onSuccess: t7,
      onError: t8
    };
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  return useMutation(t9);
}
function _temp6(error) {
  return error instanceof Error ? error.message : "Failed to merge pull request";
}
function _temp5(outcome) {
  return MERGE_SUCCESS_TOASTS[outcome.status];
}
function _temp4(pr) {
  return {
    ...pr,
    state: "closed" as const,
    mergedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}
function _temp3(current_1) {
  return recomputePrV2MergeabilityStatus(current_1, _temp2);
}
function _temp2(status) {
  return {
    ...status,
    draft: false,
    state: {
      ...status.state,
      merged: true,
      prState: "closed",
      mergeable: false
    }
  };
}
function _temp(current) {
  return current ? {
    ...current,
    pullRequest: {
      ...current.pullRequest,
      merged: true,
      draft: false,
      state: "closed"
    }
  } : current;
}